import type { AvailabilityRequest, AvailabilityResponse, ApiErrorResponse } from '../types';

/**
 * ============================================================================
 * Shared utilities for rate limiting, caching, and 429 error handling
 * ============================================================================
 */

/**
 * Extracts wait time from Retry-After header or uses exponential backoff
 */
function getWaitTime(retryAfter: string | null, retryCount: number, baseDelay: number = 1000): number {
  if (retryAfter) {
    return parseInt(retryAfter, 10) * 1000;
  }
  return baseDelay * Math.pow(2, retryCount);
}

/**
 * Rate limiter that ensures minimum time between requests
 */
class RateLimiter {
  private lastRequestTime: number = 0;

  constructor(private minDelayMs: number) {}

  async waitIfNeeded(): Promise<void> {
    const timeSinceLastRequest = Date.now() - this.lastRequestTime;
    if (timeSinceLastRequest < this.minDelayMs) {
      const waitTime = this.minDelayMs - timeSinceLastRequest;
      await new Promise(resolve => setTimeout(resolve, waitTime));
    }
    this.lastRequestTime = Date.now();
  }
}

/**
 * Cache with TTL support for success/failure differentiation
 */
class TTLCache<T> {
  private cache = new Map<string, { value: T; timestamp: number; isFailure?: boolean }>();

  constructor(
    private successTTL: number,
    private failureTTL: number
  ) {}

  get(key: string): T | null {
    const cached = this.cache.get(key);
    if (!cached) return null;

    const cacheAge = Date.now() - cached.timestamp;
    const cacheTTL = cached.isFailure ? this.failureTTL : this.successTTL;

    if (cacheAge < cacheTTL) {
      return cached.value;
    }

    // Cache expired, remove it
    this.cache.delete(key);
    return null;
  }

  set(key: string, value: T, isFailure: boolean = false): void {
    this.cache.set(key, { value, timestamp: Date.now(), isFailure });
  }
}

/**
 * Checks if an error is a CORS error (non-retryable)
 */
function isCORSError(error: unknown): boolean {
  if (error instanceof TypeError) {
    const message = error.message.toLowerCase();
    return message.includes('cors') || 
           message.includes('access-control-allow-origin') ||
           message.includes('failed to fetch');
  }
  return false;
}

/**
 * Retries a fetch operation with exponential backoff and 429 handling
 * CORS errors are not retried as they won't be resolved by retrying
 */
async function fetchWithRetry(
  url: string,
  options: RequestInit = {},
  config: {
    maxRetries?: number;
    baseDelay?: number;
    serviceName?: string;
  } = {}
): Promise<Response> {
  const {
    maxRetries = 3,
    baseDelay = 1000,
    serviceName = 'API',
  } = config;

  let retryCount = 0;

  while (retryCount <= maxRetries) {
    try {
      const response = await fetch(url, options);

      // Handle 429 errors
      if (response.status === 429) {
        const retryAfter = response.headers.get('Retry-After');
        const waitTime = getWaitTime(retryAfter, retryCount, baseDelay);
        if (retryCount < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, waitTime));
          retryCount++;
          continue;
        } else {
          throw new Error(`${serviceName} rate limited. Max retries reached.`);
        }
      }

      if (response.status >= 500 && retryCount < maxRetries) {
        const waitTime = baseDelay * Math.pow(2, retryCount);
        await new Promise(resolve => setTimeout(resolve, waitTime));
        retryCount++;
        continue;
      }

      return response;
    } catch (error) {
      if (isCORSError(error)) throw error;
      if (retryCount < maxRetries) {
        const waitTime = baseDelay * Math.pow(2, retryCount);
        await new Promise(resolve => setTimeout(resolve, waitTime));
        retryCount++;
        continue;
      } else {
        throw error;
      }
    }
  }

  throw new Error(`${serviceName} request failed after ${maxRetries} retries`);
}

/**
 * ============================================================================
 * API Configuration
 * ============================================================================
 */

/**
 * Allowed origins for /standardized/api/guest/* (backend CORS).
 * Production: ondiem.com, HubSpot, hub, ondiem-staging.webflow.io, Darby, pro-availability-widget.netlify.app.
 */
const PRODUCTION_API_ORIGINS = [
  'ondiem.com',
  'www.ondiem.com',
  '20090498.hubspotpreview-na1.com',
  'hub.ondiem.com',
  'ondiem-staging.webflow.io',
  'darby.com',
  'www.darby.com',
  'darbydental.com',
  'www.darbydental.com',
  'mcstaging.darbydental.com',
  'pro-availability-widget.netlify.app', // widget demo + branch deploys (*--pro-availability-widget.netlify.app)
];

function getApiBase(): string {
  const host = window.location.hostname.toLowerCase();
  const isLocalhost = host === 'localhost' || host === '127.0.0.1' || host.startsWith('192.168.') || host.startsWith('10.') || host.startsWith('172.');

  if (isLocalhost) {
    const useStaging = new URLSearchParams(window.location.search).get('env') === 'staging';
    return useStaging ? '/api-staging' : '/api';
  }

  // Production-whitelisted origins (and *.ondiem.com, *--pro-availability-widget.netlify.app)
  if (
    host.endsWith('.ondiem.com') ||
    PRODUCTION_API_ORIGINS.some((origin) => host === origin || host.endsWith('.' + origin) || host.includes('--' + origin))
  ) {
    return 'https://api.ondiem.com';
  }

  // Unknown host: default to production (partners may be added to backend later)
  return 'https://api.ondiem.com';
}

const API_BASE = getApiBase();
const API_URL = `${API_BASE}/standardized/api/guest/get_professional_availability`;

const API_HEADERS = {
  Accept: 'application/vnd.ondiem.v1+json',
  'Content-Type': 'application/vnd.ondiem.v1+json',
};

/**
 * Rack::Attack (backend) throttles /standardized/api/guest/* at 10 req/min per IP.
 * Limit is per IP across all pages and origins (your site + 3rd party embeds).
 * We comply by: (1) caching responses, (2) throttling to ≥6s between requests,
 * (3) retrying 429 after Retry-After. Zip input is debounced in the widget.
 */
const AVAILABILITY_CACHE = new TTLCache<AvailabilityResponse>(180000, 0); // 3 min — fewer requests when widget is on many pages
const AVAILABILITY_RATE_LIMITER = new RateLimiter(6000); // ≥6s between requests → max 10/min

/**
 * API error class for better error handling
 */
export class AvailabilityApiError extends Error {
  constructor(
    public status: number,
    public errorCode?: string,
    public errorData?: ApiErrorResponse['error_data'],
    message?: string
  ) {
    super(message || `API error: ${status}`);
    this.name = 'AvailabilityApiError';
  }
}

function availabilityCacheKey(payload: AvailabilityRequest): string {
  return `availability:${payload.start_date}:${payload.end_date}:${payload.zip_code ?? ''}:${payload.position ?? ''}`;
}

/**
 * Fetches professional availability from the guest API.
 * Respects Rack::Attack limit (10 req/min per IP): cache, throttle, retry 429 with Retry-After.
 */
export async function fetchAvailability(
  request: AvailabilityRequest
): Promise<AvailabilityResponse> {
  const payload: AvailabilityRequest = {
    start_date: request.start_date,
    end_date: request.end_date,
    zip_code: request.zip_code ?? null,
    position: request.position ?? null,
  };

  const cacheKey = availabilityCacheKey(payload);
  const cached = AVAILABILITY_CACHE.get(cacheKey);
  if (cached !== null) return cached;

  await AVAILABILITY_RATE_LIMITER.waitIfNeeded();

  const maxAttempts = 3; // initial + 2 retries on 429
  let last429Error: AvailabilityApiError | null = null;

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    try {
      const response = await fetch(API_URL, {
        method: 'POST',
        headers: API_HEADERS,
        body: JSON.stringify(payload),
      });

      if (response.status === 429) {
        const retryAfter = response.headers.get('Retry-After');
        const waitMs = (retryAfter ? parseInt(retryAfter, 10) : 60) * 1000;
        last429Error = new AvailabilityApiError(
          429,
          'rate_limit',
          { value: Math.ceil(waitMs / 1000) },
          `Rate limit exceeded. Retrying after ${Math.ceil(waitMs / 1000)}s.`
        );
        if (attempt < maxAttempts - 1) {
          await new Promise((resolve) => setTimeout(resolve, waitMs));
          continue;
        }
        throw last429Error;
      }

      if (response.status === 400) {
        let errorData: ApiErrorResponse | null = null;
        try {
          errorData = await response.json();
        } catch {
          // ignore
        }
        if (errorData) {
          throw new AvailabilityApiError(
            400,
            errorData.error_code,
            errorData.error_data,
            `API validation error: ${errorData.error_code}`
          );
        }
        throw new AvailabilityApiError(400, undefined, undefined, 'Invalid request');
      }

      if (response.status >= 500) {
        throw new AvailabilityApiError(
          500,
          'unexpected_error',
          undefined,
          'Server error. Please try again later.'
        );
      }

      if (!response.ok) {
        throw new AvailabilityApiError(
          response.status,
          undefined,
          undefined,
          `API error: ${response.status}`
        );
      }

      const data = await response.json();
      AVAILABILITY_CACHE.set(cacheKey, data, false);
      return data;
    } catch (error) {
      if (error instanceof AvailabilityApiError) throw error;
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new AvailabilityApiError(0, 'network_error', undefined, 'Network error. Please check your connection.');
      }
      throw new AvailabilityApiError(
        0,
        'unknown_error',
        undefined,
        error instanceof Error ? error.message : 'Unknown error occurred'
      );
    }
  }

  throw last429Error ?? new AvailabilityApiError(429, 'rate_limit', undefined, 'Rate limit exceeded.');
}

/**
 * ============================================================================
 * Browser geolocation + reverse geocoding (no IP API)
 * ============================================================================
 * Uses navigator.geolocation.getCurrentPosition() (user must allow), then
 * reverse-geocodes lat/lon to zip via Nominatim (OSM). That zip is passed to the
 * availability API as zip_code in the payload (never lat/lon).
 */

const GEOLOCATION_CACHE_KEY = 'geolocation-zip';
const GEOLOCATION_CACHE = new TTLCache<string | null>(3600000, 300000); // 1h success, 5min failure
const ZIP_RE = /^\d{5}$/;

function isGeolocationAvailable(): boolean {
  return typeof navigator !== 'undefined' && typeof navigator.geolocation === 'object' && typeof navigator.geolocation.getCurrentPosition === 'function';
}

/**
 * Reverse-geocode lat/lon to US zip using Nominatim (OSM). No call to OnDiem API.
 * The resulting zip is what we pass to the availability API as zip_code in the payload.
 */
async function reverseGeocodeToZip(lat: number, lon: number): Promise<string | null> {
  try {
    const url = `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lon}&format=json&addressdetails=1`;
    const res = await fetch(url, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'User-Agent': 'OnDiem-ProAvailabilityWidget/1.0 (https://ondiem.com)',
      },
    });
    if (!res.ok) return null;
    const data = await res.json();
    const postcode = data?.address?.postcode ?? data?.address?.postalcode ?? null;
    if (postcode && ZIP_RE.test(String(postcode))) return String(postcode).trim();
  } catch {
    // ignore
  }
  return null;
}

/**
 * Gets zip code using browser geolocation (user permission) + reverse geocoding.
 * No IP API used. Returns null if permission denied, unavailable, or reverse lookup fails.
 */
export function getZipFromGeolocation(): Promise<string | null> {
  const cached = GEOLOCATION_CACHE.get(GEOLOCATION_CACHE_KEY);
  if (cached !== null) return Promise.resolve(cached);

  if (!isGeolocationAvailable()) {
    GEOLOCATION_CACHE.set(GEOLOCATION_CACHE_KEY, null, true);
    return Promise.resolve(null);
  }

  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        const zip = await reverseGeocodeToZip(latitude, longitude);
        GEOLOCATION_CACHE.set(GEOLOCATION_CACHE_KEY, zip, !zip);
        resolve(zip);
      },
      () => {
        GEOLOCATION_CACHE.set(GEOLOCATION_CACHE_KEY, null, true);
        resolve(null);
      },
      { timeout: 10000, maximumAge: 300000, enableHighAccuracy: false }
    );
  });
}

/**
 * ============================================================================
 * Zippopotam.us API Service
 * 
 * Zippopotam.us is a free service with rate limits. To avoid 429 errors:
 * - Use conservative throttling (15+ seconds between requests recommended)
 * - Cache results aggressively (24 hours for successful, 30 minutes for failures)
 * - Implement exponential backoff with Retry-After header support
 * - Prevent concurrent requests
 * ============================================================================
 */

const ZIP_LOOKUP_CACHE = new TTLCache<string | null>(86400000, 1800000); // 24h success, 30min failure
const ZIP_LOOKUP_RATE_LIMITER = new RateLimiter(15000); // 15 seconds between requests (conservative)
let pendingZipLookup: Promise<string | null> | null = null;

/**
 * Looks up city/state from zip code using zippopotam.us
 * Implements rate limiting, caching, exponential backoff, and prevents concurrent requests
 */
export async function lookupCityState(zip: string): Promise<string | null> {
  const ZIP_RE = /^\d{5}$/;
  const zipStr = String(zip || '').trim();

  if (!ZIP_RE.test(zipStr)) return null;

  // Check cache first
  const cached = ZIP_LOOKUP_CACHE.get(zipStr);
  if (cached !== null) {
    return cached;
  }

  // Prevent concurrent requests - if one is pending, wait for it
  if (pendingZipLookup) {
    try {
      return await pendingZipLookup;
    } catch {
      // If pending request fails, continue with new request
    }
  }

  // Create a new lookup promise
  pendingZipLookup = (async (): Promise<string | null> => {
    try {
      // Rate limiting
      await ZIP_LOOKUP_RATE_LIMITER.waitIfNeeded();

      // Fetch with retry logic
      const response = await fetchWithRetry(`https://api.zippopotam.us/us/${zipStr}`, {}, {
        maxRetries: 3,
        baseDelay: 1000,
        serviceName: 'Zippopotamus',
      });

      if (!response.ok && response.status < 500) {
        ZIP_LOOKUP_CACHE.set(zipStr, null, true);
        return null;
      }

      // Success - parse and cache the result
      const data = await response.json();
      const place = data?.places?.[0];
      const label = place
        ? `${place['place name']}, ${place['state abbreviation']}`
        : null;

      ZIP_LOOKUP_CACHE.set(zipStr, label, false);
      return label;
    } catch {
      ZIP_LOOKUP_CACHE.set(zipStr, null, true);
      return null;
    } finally {
      // Clear pending request after completion
      pendingZipLookup = null;
    }
  })();

  return await pendingZipLookup;
}
